import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"

// Service public d'émargement par QR code — aucune authentification.
// Le stagiaire scanne le QR projeté en salle et atterrit sur /a/:token.
// Endpoints publics : GET /api/v1/a/:token/match et POST /api/v1/a/:token/sign.

const BASE = import.meta.env.VITE_API_URL || ""

export interface AttendanceMatch {
  registration_id: string
  display_name: string
}

/** Organisme responsable de la feuille, nommé au stagiaire (RGPD art. 13). */
export type PublicAttendanceOrganization = {
  name: string
  privacyNoticeUrl: O.Option<string>
  publicContactEmail: O.Option<string>
  publicContactPhone: O.Option<string>
}

export interface MatchResponse {
  matches: AttendanceMatch[]
  /** Absent d'une réponse ancienne : la page tait alors l'organisme. */
  organization: O.Option<PublicAttendanceOrganization>
}

type WireRecord = Readonly<Record<string, unknown>>

function isWireRecord(value: unknown): value is WireRecord {
  return typeof value === "object" && value instanceof Object && !Array.isArray(value)
}

/** Texte non vide d'un champ servi, sinon `O.none`. */
function wireText(fields: WireRecord, key: string): O.Option<string> {
  return pipe(
    O.fromNullable(fields[key]),
    O.filter((value): value is string => typeof value === "string"),
    O.filter((text) => text.trim() !== ""),
  )
}

/** Décode l'organisme à la frontière : `null` devient `O.none`. */
function decodeOrganization(wire: unknown): O.Option<PublicAttendanceOrganization> {
  return pipe(
    O.fromNullable(wire),
    O.filter(isWireRecord),
    O.chain((fields) =>
      pipe(
        wireText(fields, "name"),
        O.map((name) => ({
          name,
          privacyNoticeUrl: wireText(fields, "privacy_notice_url"),
          publicContactEmail: wireText(fields, "public_contact_email"),
          publicContactPhone: wireText(fields, "public_contact_phone"),
        })),
      ),
    ),
  )
}

function isAttendanceMatchList(value: unknown): value is AttendanceMatch[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) => isWireRecord(item) && typeof item.registration_id === "string" && typeof item.display_name === "string",
    )
  )
}

/** Décode la réponse de recherche à la frontière. */
function decodeMatchResponse(wire: unknown): MatchResponse {
  const fields = pipe(O.fromNullable(wire), O.filter(isWireRecord))
  return {
    matches: pipe(
      fields,
      O.chain((response) => O.fromNullable(response.matches)),
      O.filter(isAttendanceMatchList),
      O.getOrElse((): AttendanceMatch[] => []),
    ),
    organization: pipe(
      fields,
      O.chain((response) => decodeOrganization(response.organization)),
    ),
  }
}

export interface SignAttendancePayload {
  registration_id: string
  signature_data?: string
  typed_name?: string
  internal_rules_acknowledged: boolean
}

export interface SignResponse {
  signed: boolean
  display_name: string
}

interface ApiErrorBody {
  error?: { code?: string; message?: string }
  message?: string
}

/**
 * Erreur API publique portant le status HTTP (404 = token révoqué/expiré,
 * 409 = déjà signé) et le code du Problem JSON (`ATTENDANCE_RECORDS_LOCKED`
 * = feuille finalisée, refus définitif).
 */
export class PublicAttendanceError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: O.Option<string> = O.none,
    /** Corps d'erreur brut (Problem JSON) : `details` porte le contexte du refus. */
    public readonly body: unknown = {},
  ) {
    super(message)
    this.name = "PublicAttendanceError"
  }
}

/**
 * Écran définitif d'un refus de l'API publique, quelle que soit la requête
 * (sonde du jeton, recherche, signature) : une feuille finalisée renvoie 422
 * ATTENDANCE_RECORDS_LOCKED (« feuille finalisée ») ; une feuille d'un autre
 * jour 422 ATTENDANCE_SIGN_WRONG_DAY dès la recherche (« pas le jour ») ; le
 * 404 est réservé au jeton inconnu, expiré ou révoqué (« lien expiré ») ;
 * un organisme fermé répond 410 TENANT_CLOSED (« service indisponible »).
 *
 * @example
 * publicAttendanceTerminalStep(new Error("réseau")) // O.none
 */
export function publicAttendanceTerminalStep(
  err: unknown,
): O.Option<"locked" | "wrong-day" | "expired" | "closed"> {
  if (!(err instanceof PublicAttendanceError)) return O.none
  if (O.exists((code) => code === "TENANT_CLOSED")(err.code)) return O.some("closed")
  if (O.exists((code) => code === "ATTENDANCE_RECORDS_LOCKED")(err.code)) return O.some("locked")
  if (O.exists((code) => code === "ATTENDANCE_SIGN_WRONG_DAY")(err.code)) return O.some("wrong-day")
  if (err.status === 404) return O.some("expired")
  return O.none
}

/** Code machine d'un corps d'erreur : `code` à la racine (Problem JSON) ou sous `error`. */
export function errorBodyCode(body: unknown): O.Option<string> {
  const field = (value: unknown, key: string): O.Option<unknown> =>
    pipe(
      O.fromNullable(value),
      O.filter((v): v is Record<string, unknown> => typeof v === "object"),
      O.chain((record) => O.fromNullable(record[key])),
    )
  return pipe(
    field(body, "code"),
    O.alt(() => pipe(field(body, "error"), O.chain((nested) => field(nested, "code")))),
    O.filter((code): code is string => typeof code === "string" && code.length > 0),
  )
}

function rejectWithApiError(res: Response): Promise<never> {
  return res
    .json()
    .catch((): ApiErrorBody => ({ error: { message: "Erreur réseau" } }))
    .then((err: ApiErrorBody) => {
      const message = err.error?.message || err.message || "Erreur de chargement"
      throw new PublicAttendanceError(message, res.status, errorBodyCode(err), err)
    })
}

export function matchPublicAttendance(token: string, q: string): Promise<MatchResponse> {
  return fetch(`${BASE}/api/v1/a/${token}/match?q=${encodeURIComponent(q)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  })
    .then((res) => (res.ok ? res.json() : rejectWithApiError(res)))
    .then(decodeMatchResponse)
}

export function signPublicAttendance(token: string, payload: SignAttendancePayload): Promise<SignResponse> {
  return fetch(`${BASE}/api/v1/a/${token}/sign`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // Le middleware CSRF exige ce header sur tout POST /api/v1/* hors /api/v1/public/.
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(payload),
  }).then((res) => {
    if (!res.ok) return rejectWithApiError(res)
    return res.json() as Promise<SignResponse>
  })
}
